export const MAX_TRACE_PERIOD_MS = 24 * 60 * 60 * 1000;
export const DEFAULT_TRACE_HOURS = 1;
export const OPERATOR_ONLINE_MS = 2 * 60 * 1000;

export function getTracePeriodForHours(hours, now = new Date()) {
  const end = new Date(now);
  const start = new Date(end.getTime() - Number(hours) * 60 * 60 * 1000);
  return { start, end };
}

export function getDefaultTracePeriod(now = new Date()) {
  return getTracePeriodForHours(DEFAULT_TRACE_HOURS, now);
}

export function validateTracePeriod(start, end) {
  if (!start || !end) {
    return "Selecione início e fim do período.";
  }
  if (end <= start) {
    return "O fim deve ser posterior ao início.";
  }
  if (end.getTime() - start.getTime() > MAX_TRACE_PERIOD_MS) {
    return "O período máximo é de 24 horas.";
  }
  return null;
}

export function flattenPathReportItem(item) {
  const points = [];
  const path = item && item.c && item.c.path;

  if (!Array.isArray(path)) {
    return points;
  }

  for (const segment of path) {
    if (!Array.isArray(segment)) {
      continue;
    }
    for (const point of segment) {
      if (point && point.lat != null && point.lng != null) {
        points.push({ lat: point.lat, lng: point.lng });
      }
    }
  }

  return points;
}

export function flattenPathReportToTraces(reportData, equipmentById = {}) {
  const traces = {};
  const identToId = {};

  Object.values(equipmentById).forEach((equipment) => {
    if (equipment && equipment.ident != null && equipment.id != null) {
      identToId[equipment.ident] = equipment.id;
    }
  });

  if (!Array.isArray(reportData)) {
    return traces;
  }

  for (const item of reportData) {
    if (!item || item.ident == null) {
      continue;
    }

    const equipmentId = identToId[item.ident];
    if (equipmentId == null) {
      continue;
    }

    traces[equipmentId] = flattenPathReportItem(item);
  }

  return traces;
}

export function countTracePoints(equipment) {
  return Object.values(equipment || {}).reduce((total, item) => {
    const points = item.last_posi_point;
    return total + (Array.isArray(points) ? points.length : 0);
  }, 0);
}

export function sameMapPoint(a, b) {
  if (!a || !b) return false;
  return Number(a.lat) === Number(b.lat) && Number(a.lng) === Number(b.lng);
}

export function appendMovedPoint(points, nextPoint) {
  const list = Array.isArray(points) ? points.slice() : [];
  if (!nextPoint || nextPoint.lat == null || nextPoint.lng == null) {
    return list;
  }
  const next = { lat: Number(nextPoint.lat), lng: Number(nextPoint.lng) };
  if (list.length > 0 && sameMapPoint(list[list.length - 1], next)) {
    return list;
  }
  list.push(next);
  return list;
}

export function isOperatorOnline(timestamp, now = new Date()) {
  if (!timestamp) return false;
  const parsed = new Date(timestamp);
  if (Number.isNaN(parsed.getTime())) return false;
  return now.getTime() - parsed.getTime() <= OPERATOR_ONLINE_MS;
}

export function operatorPositionToPoint(position) {
  if (!position) return null;
  const lat = Number(position.latitude != null ? position.latitude : position.lat);
  const lng = Number(position.longitude != null ? position.longitude : position.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (Math.abs(lat) < 0.000001 && Math.abs(lng) < 0.000001) return null;
  return { lat, lng };
}

export function groupOperatorTrailPoints(positions) {
  const trails = {};
  const list = Array.isArray(positions) ? positions.slice() : [];
  list.sort((a, b) => {
    const ta = new Date(a && a.timestamp).getTime() || 0;
    const tb = new Date(b && b.timestamp).getTime() || 0;
    return ta - tb;
  });

  list.forEach((position) => {
    const operatorId = position && position.operator;
    const point = operatorPositionToPoint(position);
    if (operatorId == null || !point) return;
    if (!trails[operatorId]) {
      trails[operatorId] = [];
    }
    trails[operatorId] = appendMovedPoint(trails[operatorId], point);
  });

  return trails;
}

export function buildOperatorMapState(lastKnownPositions, trailPositions, now = new Date()) {
  const operators = {};

  (lastKnownPositions || []).forEach((position) => {
    const operatorId = position && position.operator;
    const lastKnown = operatorPositionToPoint(position);
    if (operatorId == null || !lastKnown) return;
    operators[operatorId] = {
      id: operatorId,
      name: position.operator_name || position.operator_ident || String(operatorId),
      speed: position.speed,
      last_connection: position.timestamp,
      online: isOperatorOnline(position.timestamp, now),
      lastKnown,
      last_posi_point: [],
    };
  });

  const trails = groupOperatorTrailPoints(trailPositions);
  Object.keys(operators).forEach((id) => {
    operators[id] = {
      ...operators[id],
      last_posi_point: trails[id] || [],
    };
  });

  return operators;
}

export function offsetIfCoLocated(position, others, delta = 0.00014) {
  if (!position) return position;
  const conflict = (others || []).some(
    (other) => other && sameMapPoint(other, position)
  );
  if (!conflict) return position;
  return {
    lat: position.lat + delta,
    lng: position.lng + delta,
  };
}
